/**
 * Durable post-confirmation effects.
 *
 * This is the layer that would have saved the stranded PKR 4,000. The chain
 * transaction succeeding is only half the operation; the other half is telling
 * the backend about it (`createOrder`, `openDispute`, `addCollateral`, …).
 * Today that is a bare `socket.emit` with no ack, no retry and no persistence,
 * so a reload in the wrong second loses it permanently.
 *
 * Delivery is verify-then-send, not blind replay. The server's guards are
 * inconsistent — `createFiatTransaction` rejects duplicates, but
 * `giveVoteToDisputeOrder` has no already-voted check — so a naive retry could
 * double-submit a jury vote. Order of operations per attempt:
 *
 *   1. `verify()`  — did this already land? (reads endpoints that already exist)
 *   2. `send()`    — carries a deterministic `effectKey`
 *   3. ack / retry with exponential backoff
 *
 * The `effectKey` plus the server-side unique index is the actual exactly-once
 * guarantee; `verify()` closes the window where a send succeeded but the ack
 * was lost, and keeps us from leaning on server guards that may not exist.
 */

import { EVENTS } from "./machine";

const registry = new Map();

export function registerEffect(descriptor) {
  if (!descriptor || !descriptor.type) throw new Error("effect descriptor needs a type");
  registry.set(descriptor.type, descriptor);
  return descriptor;
}

export function getEffect(type) {
  return registry.get(type) || null;
}

export const BASE_BACKOFF_MS = 2000;
export const MAX_BACKOFF_MS = 60_000;
export const DEFAULT_MAX_ATTEMPTS = 8;

/** Exponential backoff, capped. Deterministic — no jitter — so tests can assert it. */
export function backoffMs(attempts) {
  if (attempts <= 0) return 0;
  return Math.min(BASE_BACKOFF_MS * 2 ** (attempts - 1), MAX_BACKOFF_MS);
}

/**
 * When may the outbox next try?
 *
 * The first attempt is held back by the descriptor's `gracePeriodMs`. That
 * exists because during migration each screen still fires its effect directly
 * on the happy path — the outbox is a BACKSTOP, not a second sender. Without a
 * grace period both would fire within milliseconds of each other; the server
 * would reject the loser with "Transaction already in progress", and the screen
 * would show a spurious "Order creation failed" toast.
 *
 * With it, the direct path wins normally, `verify()` then observes the effect
 * already landed, and the row settles without a second send. Only when the
 * direct path never happened — the case this whole system exists for — does the
 * outbox actually send.
 */
export function nextAttemptAt(entry, descriptor) {
  const attempts = entry?.effect?.attempts || 0;
  const base = entry?.updatedAt || 0;
  if (attempts === 0) return base + (descriptor?.gracePeriodMs || 0);
  return base + backoffMs(attempts);
}

export function isDue(entry, now = Date.now(), descriptor) {
  return now >= nextAttemptAt(entry, descriptor);
}

/**
 * Decide and perform the next action for one entry sitting in `effects_pending`.
 *
 * @returns {Promise<{event: string, payload: object}|null>} event to apply, or
 *          null when nothing should happen yet (backoff, or attempts exhausted).
 */
export async function processEntry({ entry, ctx, now = Date.now() }) {
  if (!entry || !entry.effect || !entry.effect.type) return null;

  const descriptor = getEffect(entry.effect.type);
  if (!descriptor) {
    // Unknown effect type — almost certainly a journal row written by a newer
    // build. Leave it alone rather than guessing.
    return null;
  }

  const attempts = entry.effect.attempts || 0;
  const maxAttempts = descriptor.maxAttempts || DEFAULT_MAX_ATTEMPTS;

  // Exhausted: stop burning requests. The row stays in the journal (not
  // terminal) so it is visible, and a later manual retry or a fresh session can
  // pick it up.
  if (attempts >= maxAttempts) return null;

  if (!isDue(entry, now, descriptor)) return null;

  const { payload, effectKey } = entry.effect;

  // --- 1. verify -----------------------------------------------------------
  if (typeof descriptor.verify === "function") {
    try {
      const already = await descriptor.verify({ payload, entry, ctx });
      if (already === true) {
        return { event: EVENTS.EFFECT_SETTLED, payload: { applied: "already" } };
      }
    } catch (err) {
      // Verification is best-effort. If we cannot tell, fall through and send —
      // the effectKey and the server gate still prevent a duplicate.
    }
  }

  // --- 2. send -------------------------------------------------------------
  try {
    const result = await descriptor.send({ payload, effectKey, entry, ctx });
    return {
      event: EVENTS.EFFECT_SETTLED,
      payload: { applied: result === "duplicate" ? "duplicate" : "applied" },
    };
  } catch (err) {
    return { event: EVENTS.EFFECT_FAILED, payload: { error: err } };
  }
}

export const __testing = {
  reset() {
    registry.clear();
  },
  registry,
};
